import { isPreferencePartition } from "@/api/scope";
import { isValidRef, type RecentRef } from "@/app/recent";
import {
  canonicalNamespaceSelection,
  namespaceSelectionBinding,
  type NamespaceSelection,
} from "@/state/namespaceSelection";

export const PREFERENCE_MARKER_KEY = "k8s-dashboard.preference-partition.v1";
export const RESOURCE_TABLE_KEY = "k8s-dashboard.preference.v1.resource-table";
export const RESOURCE_DRAWER_KEY = "k8s-dashboard.preference.v1.resource-drawer";
export const FAVORITES_KEY = "k8s-dashboard.preference.v1.favorites";
export const NAVIGATION_KEY = "k8s-dashboard.preference.v1.navigation";
export const NAMESPACE_SELECTION_KEY = "k8s-dashboard.preference.v1.namespace-selection";
export const LEGACY_NAMESPACE_STORAGE_KEY = "observer.namespace";
export const PREFERENCE_MAX_BYTES = 64 * 1024;
export const FAVORITES_MAX_BYTES = 32 * 1024;
export const NAVIGATION_MAX_BYTES = 8 * 1024;
export const NAMESPACE_SELECTION_MAX_BYTES = 8 * 1024;
export const OWNED_PREFERENCE_MAX_BYTES = 180_480;
export const PREFERENCE_MAX_RECORDS = 64;
export const FAVORITES_MAX_ITEMS = 20;
export const TABLE_MAX_COLUMNS = 64;
export const PREFERENCE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

type Marker = { version: 1; partition: string };
type RecordScope = {
  partition: string;
  clusterId: string;
  gvr: string;
  touchedAt: number;
};

export type PreferenceScope = Readonly<Pick<RecordScope, "partition" | "clusterId" | "gvr">>;
export type StoredColumn = Readonly<{ id: string; width: number; visible: boolean }>;
type TableRecord = RecordScope & { columns: StoredColumn[] };
type DrawerRecord = RecordScope & { width: number };
type FavoriteRecord = Omit<RecordScope, "gvr"> & { items: RecentRef[] };
export type NavigationPreference = Readonly<{
  groups: Readonly<Record<string, readonly string[]>>;
  shortcutsEnabled: boolean;
}>;
type NavigationRecord = { partition: string; touchedAt: number; groups: Record<string, string[]>; shortcutsEnabled: boolean };
type NamespaceSelectionRecord = {
  version: 1;
  partition: string;
  clusterId: string;
  scopeFingerprint: string;
  selection: NamespaceSelection;
  savedAt: number;
  expiresAt: number;
};
type Envelope<T> = { version: 1; records: T[] };

const OWNED_KEYS = [
  PREFERENCE_MARKER_KEY,
  RESOURCE_TABLE_KEY,
  RESOURCE_DRAWER_KEY,
  FAVORITES_KEY,
  NAVIGATION_KEY,
  NAMESPACE_SELECTION_KEY,
] as const;

const encoder = new TextEncoder();
let fencedPartition: string | undefined;
let ownedPartition: string | undefined;

/** 현재 탭이 저장소 이벤트나 marker 불일치로 잃은 partition 소유권인지 확인합니다. */
export function isPreferencePartitionFenced(partition: string): boolean {
  return fencedPartition === partition;
}
type TimedRecord = { partition: string; clusterId: string; gvr?: string; touchedAt: number };
const recordKey = (record: TimedRecord) => {
  const gvr = record.gvr ?? "";
  return `${record.partition.length}:${record.partition}${record.clusterId.length}:${record.clusterId}${gvr.length}:${gvr}`;
};

function storage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function parseMarker(raw: string | null): Marker | undefined {
  if (!raw || encoder.encode(raw).byteLength > 256) return undefined;
  try {
    const value = JSON.parse(raw) as Partial<Marker>;
    return value.version === 1 && isPreferencePartition(value.partition)
      ? { version: 1, partition: value.partition }
      : undefined;
  } catch {
    return undefined;
  }
}

function markerMatches(partition: string): boolean {
  try {
    return parseMarker(storage()?.getItem(PREFERENCE_MARKER_KEY) ?? null)?.partition === partition;
  } catch {
    return false;
  }
}

export type NamespaceSelectionPreferenceResult =
  | Readonly<{ status: "valid"; selection: NamespaceSelection; savedAt: number; expiresAt: number; legacyPresent: boolean }>
  | Readonly<{ status: "absent" | "invalid" | "unavailable"; legacyPresent: boolean }>;

function legacyNamespaceKeyPresent(target: Storage): boolean {
  try {
    return target.getItem(LEGACY_NAMESPACE_STORAGE_KEY) !== null;
  } catch {
    return true;
  }
}

function parseNamespaceSelectionRecord(value: unknown): NamespaceSelectionRecord | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const record = value as Partial<NamespaceSelectionRecord>;
  const keys = Object.keys(value);
  const selection = record.selection ? canonicalNamespaceSelection(record.selection) : undefined;
  if (keys.length !== 7 || !["version", "partition", "clusterId", "scopeFingerprint", "selection", "savedAt", "expiresAt"].every((key) => keys.includes(key)) ||
      record.version !== 1 || !isPreferencePartition(record.partition) ||
      typeof record.clusterId !== "string" || record.clusterId.length === 0 || record.clusterId.length > 256 ||
      typeof record.scopeFingerprint !== "string" || record.scopeFingerprint.length === 0 ||
      !selection || typeof record.savedAt !== "number" || !Number.isSafeInteger(record.savedAt) || record.savedAt < 0 ||
      typeof record.expiresAt !== "number" || !Number.isSafeInteger(record.expiresAt) ||
      record.expiresAt !== record.savedAt + PREFERENCE_TTL_MS) return undefined;
  return { ...record as NamespaceSelectionRecord, selection };
}

/**
 * 전역 namespace 선택 record를 읽습니다. TTL은 읽을 때 갱신하지 않습니다.
 * raw legacy key는 값 자체를 읽거나 선택·초안으로 해석하지 않고 존재 여부만 봅니다.
 */
export function readNamespaceSelectionPreference(
  identity: Readonly<{ partition: string; clusterId: string; scopeFingerprint: string }>,
  now = Date.now(),
): NamespaceSelectionPreferenceResult {
  if (fencedPartition === identity.partition || ownedPartition !== identity.partition || !markerMatches(identity.partition)) {
    return { status: "unavailable", legacyPresent: true };
  }
  const target = storage();
  if (!target) return { status: "unavailable", legacyPresent: true };
  const legacyPresent = legacyNamespaceKeyPresent(target);
  try {
    const raw = target.getItem(NAMESPACE_SELECTION_KEY);
    if (raw === null) return { status: "absent", legacyPresent };
    if (raw.length > NAMESPACE_SELECTION_MAX_BYTES || encoder.encode(raw).byteLength > NAMESPACE_SELECTION_MAX_BYTES) {
      return { status: "invalid", legacyPresent };
    }
    const record = parseNamespaceSelectionRecord(JSON.parse(raw));
    if (!record || record.partition !== identity.partition || record.clusterId !== identity.clusterId ||
        record.scopeFingerprint !== identity.scopeFingerprint || record.savedAt > now || record.expiresAt <= now) {
      return { status: "invalid", legacyPresent };
    }
    return { status: "valid", selection: record.selection, savedAt: record.savedAt, expiresAt: record.expiresAt, legacyPresent };
  } catch {
    return { status: "invalid", legacyPresent };
  }
}

/** 저장·동일 바이트 read-back·현재 marker 확인 뒤에만 raw legacy key 하나를 정리합니다. */
export function writeNamespaceSelectionPreference(
  identity: Readonly<{ partition: string; clusterId: string; scopeFingerprint: string }>,
  selection: NamespaceSelection,
  now = Date.now(),
): boolean {
  if (fencedPartition === identity.partition || ownedPartition !== identity.partition || !markerMatches(identity.partition) ||
      !Number.isSafeInteger(now) || now < 0 || !Number.isSafeInteger(now + PREFERENCE_TTL_MS)) return false;
  const canonical = canonicalNamespaceSelection(selection);
  const target = storage();
  if (!canonical || !target) return false;
  const record: NamespaceSelectionRecord = {
    version: 1,
    ...identity,
    selection: canonical,
    savedAt: now,
    expiresAt: now + PREFERENCE_TTL_MS,
  };
  const raw = JSON.stringify(record);
  if (raw.length > NAMESPACE_SELECTION_MAX_BYTES || encoder.encode(raw).byteLength > NAMESPACE_SELECTION_MAX_BYTES ||
      fencedPartition === identity.partition || ownedPartition !== identity.partition || !markerMatches(identity.partition)) return false;
  try {
    if (fencedPartition === identity.partition || ownedPartition !== identity.partition || !markerMatches(identity.partition)) return false;
    target.setItem(NAMESPACE_SELECTION_KEY, raw);
    if (fencedPartition === identity.partition || ownedPartition !== identity.partition ||
        !markerMatches(identity.partition) || target.getItem(NAMESPACE_SELECTION_KEY) !== raw) return false;
    const verified = parseNamespaceSelectionRecord(JSON.parse(target.getItem(NAMESPACE_SELECTION_KEY) ?? ""));
    if (!verified || namespaceSelectionBinding(verified.selection) !== namespaceSelectionBinding(canonical)) return false;
    if (fencedPartition === identity.partition || ownedPartition !== identity.partition ||
        !markerMatches(identity.partition)) return false;
    target.removeItem(LEGACY_NAMESPACE_STORAGE_KEY);
    return fencedPartition !== identity.partition && ownedPartition === identity.partition &&
      markerMatches(identity.partition) && target.getItem(NAMESPACE_SELECTION_KEY) === raw;
  } catch {
    return false;
  }
}

/** 알려진 marker만 설정합니다. 다른 계정 record를 열거하거나 채택하지 않습니다. */
export function activatePreferencePartition(partition: string): boolean {
  if (!isPreferencePartition(partition)) return false;
  const verifiedIdentityTransition = ownedPartition !== undefined && ownedPartition !== partition;
  /* 다른 탭이 새 marker를 쓴 뒤에도 이전 탭은 옛 자격의 응답을 받을 수 있습니다.
     그 탭은 같은 옛 partition을 되찾을 수 없습니다. 새로 검증된 다른 partition이면
     차단을 풀고 아래의 일반 mismatch 정리 계약을 따릅니다. */
  if (fencedPartition === partition) return false;
  if (fencedPartition !== undefined) fencedPartition = undefined;
  const target = storage();
  if (!target) return false;
  try {
    const current = parseMarker(target.getItem(PREFERENCE_MARKER_KEY));
    if (current?.partition !== partition) {
      /* 계정/domain mismatch가 확인됐으므로 새 marker를 쓰기 전에 작은 소유 key 집합만
         지웁니다. 새로 검증된 다른 identity로 전환할 때만 옛 탭 소유권을 내려놓습니다.
         그 전에는 A 탭이 외부 B bytes를 지울 수 없습니다. localStorage를 열거하지 않습니다. */
      if (verifiedIdentityTransition) ownedPartition = undefined;
      if (!clearOwnedPreferences()) return false;
    }
    target.setItem(PREFERENCE_MARKER_KEY, JSON.stringify({ version: 1, partition } satisfies Marker));
    const active = markerMatches(partition);
    if (active) ownedPartition = partition;
    return active;
  } catch {
    return false;
  }
}

/** 인증 generation 중단 뒤 같은 partition을 다시 여는 비파괴 경로입니다.
 * marker가 그대로일 때만 재개하고, 다른 탭의 marker·record는 지우거나 채택하지 않습니다. */
export function resumePreferencePartition(partition: string): boolean {
  if (!isPreferencePartition(partition) || fencedPartition === partition) return false;
  const target = storage();
  if (!target) return false;
  try {
    const current = parseMarker(target.getItem(PREFERENCE_MARKER_KEY));
    if (current?.partition === partition) {
      ownedPartition = partition;
      return true;
    }
    if (current) fencedPartition = partition;
    return false;
  } catch {
    return false;
  }
}

export function watchPreferencePartition(partition: string, onMismatch: () => void): () => void {
  const listener = (event: StorageEvent) => {
    if (event.storageArea === storage() && event.key === PREFERENCE_MARKER_KEY) {
      if (parseMarker(event.newValue)?.partition !== partition) {
        fencedPartition = partition;
        onMismatch();
      }
    }
  };
  window.addEventListener("storage", listener);
  return () => window.removeEventListener("storage", listener);
}

/** 명시적 sign-out/expiry privacy 경계입니다. key 열거와 localStorage.clear()는 하지 않습니다. */
export function clearOwnedPreferences(): boolean {
  /* 새 marker를 본 옛 탭은 marker와 record를 지울 권한도 잃습니다. 메모리 상태는
     호출부가 비우되, 이 함수는 새 계정 bytes를 단 하나도 건드리지 않습니다. */
  if (fencedPartition !== undefined) return false;
  const target = storage();
  if (!target) {
    ownedPartition = undefined;
    return true;
  }
  /* storage event는 지연되거나 전달되지 않을 수 있습니다. 삭제 직전에 현재 유효 marker를
     다시 읽고, 이 탭이 활성화한 partition을 다른 탭이 교체했다면 먼저 fence합니다. */
  if (ownedPartition !== undefined) {
    try {
      const current = parseMarker(target.getItem(PREFERENCE_MARKER_KEY));
      if (current && current.partition !== ownedPartition) {
        fencedPartition = ownedPartition;
        return false;
      }
    } catch {
      return false;
    }
  }
  for (const key of OWNED_KEYS) {
    try {
      target.removeItem(key);
    } catch {
      // 저장소 접근·quota 오류 때문에 sign-out이 실패해서는 안 됩니다.
    }
  }
  ownedPartition = undefined;
  return true;
}

function validScope(value: unknown): value is RecordScope {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const record = value as Partial<RecordScope>;
  return isPreferencePartition(record.partition) &&
    typeof record.clusterId === "string" && record.clusterId.length > 0 && record.clusterId.length <= 256 &&
    typeof record.gvr === "string" && record.gvr.length > 0 && record.gvr.length <= 512 &&
    typeof record.touchedAt === "number" && Number.isSafeInteger(record.touchedAt) && record.touchedAt >= 0;
}

function parseEnvelope<T extends TimedRecord>(
  key: string,
  validate: (value: unknown) => value is T,
  now: number,
  maxBytes = PREFERENCE_MAX_BYTES,
): Envelope<T> {
  const target = storage();
  if (!target) return { version: 1, records: [] };
  try {
    const raw = target.getItem(key);
    if (!raw) return { version: 1, records: [] };
    if (encoder.encode(raw).byteLength > maxBytes) {
      target.removeItem(key);
      return { version: 1, records: [] };
    }
    const parsed = JSON.parse(raw) as Partial<Envelope<unknown>>;
    if (parsed.version !== 1 || !Array.isArray(parsed.records)) throw new Error("invalid preference envelope");
    const cutoff = now - PREFERENCE_TTL_MS;
    const records = parsed.records.filter(validate).filter((record) => record.touchedAt >= cutoff);
    return { version: 1, records: pruneRecords(records) };
  } catch {
    try {
      target.removeItem(key);
    } catch {
      // 손상된 저장소는 설정이 없는 것과 같습니다.
    }
    return { version: 1, records: [] };
  }
}

function pruneRecords<T extends TimedRecord>(records: T[]): T[] {
  return [...records]
    .sort((a, b) => b.touchedAt - a.touchedAt || recordKey(a).localeCompare(recordKey(b)))
    .slice(0, PREFERENCE_MAX_RECORDS);
}

function writeEnvelope<T extends TimedRecord>(
  key: string,
  partition: string,
  records: T[],
  maxBytes = PREFERENCE_MAX_BYTES,
): boolean {
  if (!markerMatches(partition)) return false;
  const target = storage();
  if (!target) return false;
  let bounded = pruneRecords(records);
  try {
    while (bounded.length > 0) {
      const raw = JSON.stringify({ version: 1, records: bounded } satisfies Envelope<T>);
      if (encoder.encode(raw).byteLength <= maxBytes) {
        if (!markerMatches(partition)) return false;
        target.setItem(key, raw);
        return true;
      }
      bounded = bounded.slice(0, -1);
    }
    if (!markerMatches(partition)) return false;
    target.setItem(key, JSON.stringify({ version: 1, records: [] } satisfies Envelope<T>));
    return true;
  } catch {
    return false;
  }
}

function sameScope(record: RecordScope, scope: PreferenceScope): boolean {
  return record.partition === scope.partition && record.clusterId === scope.clusterId && record.gvr === scope.gvr;
}

function validTableRecord(value: unknown): value is TableRecord {
  if (!validScope(value)) return false;
  const columns = (value as Partial<TableRecord>).columns;
  return Array.isArray(columns) && columns.length <= TABLE_MAX_COLUMNS && columns.every((column) =>
    Boolean(column) && typeof column === "object" && !Array.isArray(column) &&
    typeof (column as StoredColumn).id === "string" && (column as StoredColumn).id.length > 0 && (column as StoredColumn).id.length <= 512 &&
    typeof (column as StoredColumn).width === "number" && Number.isFinite((column as StoredColumn).width) &&
    typeof (column as StoredColumn).visible === "boolean");
}

function validDrawerRecord(value: unknown): value is DrawerRecord {
  return validScope(value) && typeof (value as Partial<DrawerRecord>).width === "number" &&
    Number.isFinite((value as Partial<DrawerRecord>).width);
}

export function readTablePreference(scope: PreferenceScope, now = Date.now()): StoredColumn[] | undefined {
  if (!markerMatches(scope.partition)) return undefined;
  const envelope = parseEnvelope(RESOURCE_TABLE_KEY, validTableRecord, now);
  const found = envelope.records.find((record) => sameScope(record, scope));
  if (!found) {
    writeEnvelope(RESOURCE_TABLE_KEY, scope.partition, envelope.records);
    return undefined;
  }
  const touched = { ...found, touchedAt: now };
  writeEnvelope(RESOURCE_TABLE_KEY, scope.partition, [touched, ...envelope.records.filter((record) => !sameScope(record, scope))]);
  return found.columns.map((column) => ({ ...column }));
}

export function writeTablePreference(scope: PreferenceScope, columns: readonly StoredColumn[], now = Date.now()): boolean {
  if (columns.length > TABLE_MAX_COLUMNS || !markerMatches(scope.partition)) return false;
  const envelope = parseEnvelope(RESOURCE_TABLE_KEY, validTableRecord, now);
  const record: TableRecord = { ...scope, touchedAt: now, columns: columns.map((column) => ({ ...column })) };
  return validTableRecord(record) && writeEnvelope(
    RESOURCE_TABLE_KEY,
    scope.partition,
    [record, ...envelope.records.filter((item) => !sameScope(item, scope))],
  );
}

export function readDrawerPreference(scope: PreferenceScope, now = Date.now()): number | undefined {
  if (!markerMatches(scope.partition)) return undefined;
  const envelope = parseEnvelope(RESOURCE_DRAWER_KEY, validDrawerRecord, now);
  const found = envelope.records.find((record) => sameScope(record, scope));
  if (!found) {
    writeEnvelope(RESOURCE_DRAWER_KEY, scope.partition, envelope.records);
    return undefined;
  }
  writeEnvelope(RESOURCE_DRAWER_KEY, scope.partition, [
    { ...found, touchedAt: now },
    ...envelope.records.filter((record) => !sameScope(record, scope)),
  ]);
  return found.width;
}

export function writeDrawerPreference(scope: PreferenceScope, width: number, now = Date.now()): boolean {
  if (!Number.isFinite(width) || !markerMatches(scope.partition)) return false;
  const envelope = parseEnvelope(RESOURCE_DRAWER_KEY, validDrawerRecord, now);
  const record: DrawerRecord = { ...scope, touchedAt: now, width };
  return writeEnvelope(
    RESOURCE_DRAWER_KEY,
    scope.partition,
    [record, ...envelope.records.filter((item) => !sameScope(item, scope))],
  );
}

export type FavoriteScope = Readonly<{ partition: string; clusterId: string }>;

function validFavoriteRecord(value: unknown): value is FavoriteRecord {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const record = value as Partial<FavoriteRecord>;
  return isPreferencePartition(record.partition) &&
    typeof record.clusterId === "string" && record.clusterId.length > 0 && record.clusterId.length <= 256 &&
    typeof record.touchedAt === "number" && Number.isSafeInteger(record.touchedAt) && record.touchedAt >= 0 &&
    Array.isArray(record.items) && record.items.length <= FAVORITES_MAX_ITEMS &&
    record.items.every((item) => isValidRef(item) && item.clusterId === record.clusterId);
}

const sameFavoriteScope = (record: FavoriteRecord, scope: FavoriteScope) =>
  record.partition === scope.partition && record.clusterId === scope.clusterId;

export function readFavorites(scope: FavoriteScope, now = Date.now()): RecentRef[] {
  if (!markerMatches(scope.partition)) return [];
  const envelope = parseEnvelope(FAVORITES_KEY, validFavoriteRecord, now, FAVORITES_MAX_BYTES);
  const found = envelope.records.find((record) => sameFavoriteScope(record, scope));
  if (!found) {
    writeEnvelope(FAVORITES_KEY, scope.partition, envelope.records, FAVORITES_MAX_BYTES);
    return [];
  }
  writeEnvelope(FAVORITES_KEY, scope.partition, [
    { ...found, touchedAt: now },
    ...envelope.records.filter((record) => !sameFavoriteScope(record, scope)),
  ], FAVORITES_MAX_BYTES);
  return found.items.map((item) => ({ ...item }));
}

export function writeFavorites(scope: FavoriteScope, items: readonly RecentRef[], now = Date.now()): boolean {
  if (items.length > FAVORITES_MAX_ITEMS || !markerMatches(scope.partition)) return false;
  const unique = new Set<string>();
  for (const item of items) {
    const id = `${item.group}/${item.version}/${item.resource}/${item.uid}`;
    if (!isValidRef(item) || item.clusterId !== scope.clusterId || unique.has(id)) return false;
    unique.add(id);
  }
  const envelope = parseEnvelope(FAVORITES_KEY, validFavoriteRecord, now, FAVORITES_MAX_BYTES);
  const record: FavoriteRecord = { ...scope, touchedAt: now, items: items.map((item) => ({ ...item })) };
  return writeEnvelope(FAVORITES_KEY, scope.partition, [
    record,
    ...envelope.records.filter((item) => !sameFavoriteScope(item, scope)),
  ], FAVORITES_MAX_BYTES);
}

function validNavigationRecord(value: unknown): value is NavigationRecord {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const record = value as Partial<NavigationRecord>;
  if (!isPreferencePartition(record.partition) || typeof record.touchedAt !== "number" ||
      !Number.isSafeInteger(record.touchedAt) || record.touchedAt < 0 || typeof record.shortcutsEnabled !== "boolean" ||
      !record.groups || typeof record.groups !== "object" || Array.isArray(record.groups)) return false;
  const entries = Object.entries(record.groups);
  const fixedGroups = new Set(["observe", "resources", "manage", "custom"]);
  const ids = entries.flatMap(([, groupIds]) => Array.isArray(groupIds) ? groupIds : []);
  return entries.length <= 4 && ids.length <= 64 && new Set(ids).size === ids.length && entries.every(([group, ids]) =>
    fixedGroups.has(group) && Array.isArray(ids) && ids.length <= 64 &&
    ids.every((id) => typeof id === "string" && id.length > 0 && id.length <= 128));
}

export function readNavigationPreference(partition: string, now = Date.now()): NavigationPreference | undefined {
  if (!markerMatches(partition)) return undefined;
  const target = storage();
  if (!target) return undefined;
  try {
    const raw = target.getItem(NAVIGATION_KEY);
    if (!raw) return undefined;
    if (encoder.encode(raw).byteLength > NAVIGATION_MAX_BYTES) throw new Error("oversize navigation preference");
    const value = JSON.parse(raw) as Partial<{ version: 1; record: unknown }>;
    if (value.version !== 1 || !validNavigationRecord(value.record) || value.record.partition !== partition ||
        value.record.touchedAt < now - PREFERENCE_TTL_MS) throw new Error("invalid navigation preference");
    const record = { ...value.record, touchedAt: now };
    if (!writeNavigationPreference(partition, record, now)) return undefined;
    return { groups: record.groups, shortcutsEnabled: record.shortcutsEnabled };
  } catch {
    try { if (markerMatches(partition)) target.removeItem(NAVIGATION_KEY); } catch { /* absent preference */ }
    return undefined;
  }
}

export function writeNavigationPreference(
  partition: string,
  preference: NavigationPreference,
  now = Date.now(),
): boolean {
  if (!markerMatches(partition)) return false;
  const record: NavigationRecord = {
    partition,
    touchedAt: now,
    groups: Object.fromEntries(Object.entries(preference.groups).map(([group, ids]) => [group, [...ids]])),
    shortcutsEnabled: preference.shortcutsEnabled === true,
  };
  if (!validNavigationRecord(record)) return false;
  const raw = JSON.stringify({ version: 1, record });
  if (encoder.encode(raw).byteLength > NAVIGATION_MAX_BYTES || !markerMatches(partition)) return false;
  try {
    storage()?.setItem(NAVIGATION_KEY, raw);
    return markerMatches(partition);
  } catch {
    return false;
  }
}
